import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Star, Quote, Car, PenLine, Loader2, ChevronDown, ChevronUp, ImagePlus, X, Camera, CornerDownRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { REVIEWS } from "../../mock";
import Reveal from "./Reveal";
import { SectionHead } from "./Services";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const INITIAL = 6;
const MAX_PHOTOS = 3;
const fieldCls = "h-11 rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0";
const empty = { name: "", car: "", rating: 5, text: "" };

// Shrink photos in the browser before upload (faster on mobile data)
const compress = (file, maxSide = 1600, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error("Not an image"));
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const Stars = ({ value, size = "h-4 w-4", onChange, hover, setHover }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((k) => {
      const filled = k <= (hover || value);
      const cls = `${size} ${filled ? "fill-brand text-brand" : "text-white/25"}`;
      return onChange ? (
        <button key={k} type="button" onClick={() => onChange(k)} onMouseEnter={() => setHover(k)} onMouseLeave={() => setHover(0)} className="transition-transform hover:scale-110" data-testid={`rating-star-${k}`} aria-label={`${k} stars`}>
          <Star className={cls} />
        </button>
      ) : <Star key={k} className={cls} />;
    })}
  </div>
);

const Reviews = () => {
  const [live, setLive] = useState([]);
  const [googleUrl, setGoogleUrl] = useState("");
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [f, setF] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(({ data }) => setLive(data)).catch(() => setLive([]));
    axios.get(`${API}/settings`).then(({ data }) => setGoogleUrl(data.google_review_url || "")).catch(() => {});
  }, []);

  const all = useMemo(() => [
    ...live.map((r) => ({ ...r, photos: r.photos.map((p) => `${BACKEND}${p}`) })),
    ...REVIEWS.map((r) => ({ ...r, rating: 5, photos: [] })),
  ], [live]);
  const avg = (all.reduce((a, r) => a + r.rating, 0) / all.length).toFixed(1);
  const visible = showAll ? all : all.slice(0, INITIAL);

  const addPhotos = async (files) => {
    const list = Array.from(files || []).slice(0, MAX_PHOTOS - photos.length);
    if (Array.from(files || []).length > list.length) toast.error(`You can add up to ${MAX_PHOTOS} photos`);
    try {
      const out = await Promise.all(list.map((file) => compress(file)));
      setPhotos((p) => [...p, ...out].slice(0, MAX_PHOTOS));
    } catch {
      toast.error("One of those files isn't a supported image.");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.car.trim() || !f.text.trim()) { toast.error("Please fill in your name, car and review."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, { ...f, photos });
      setLive((prev) => [{ ...data, isNew: true }, ...prev]);
      setOpen(false);
      setF(empty);
      setPhotos([]);
      toast.success("Thanks for your review!", { description: "It's now live on the page.", duration: 6000 });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't post your review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (showAll) sectionRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowAll((v) => !v);
  };

  return (
    <section id="reviews" ref={sectionRef} className="relative overflow-hidden bg-[#08090b] py-24 lg:py-28">
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[500px] w-[500px] rounded-full bg-brand/10 blur-[120px]" />
      <div className="container-x relative">
        <SectionHead badge="Driver Reviews" title="Trusted By Local Drivers" sub="Real feedback from drivers across North West London" />

        <Reveal className="mb-10 flex flex-col items-center justify-between gap-5 rounded-2xl border border-white/10 bg-[#111217] p-6 sm:flex-row">
          <div className="flex items-center gap-5">
            <div className="font-display text-5xl font-bold text-white" data-testid="reviews-average">{avg}</div>
            <div>
              <Stars value={Math.round(avg)} size="h-5 w-5" />
              <p className="mt-1 text-sm text-white/55" data-testid="reviews-count">Based on {all.length} reviews</p>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {googleUrl && (
              <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-lg border border-white/20 bg-white px-5 py-3 font-semibold text-[#1f1f1f] transition-colors hover:bg-white/90" data-testid="google-review-btn">
                <span className="font-display text-lg font-bold leading-none"><span className="text-[#4285F4]">G</span></span>
                Review us on Google <ExternalLink className="h-3.5 w-3.5 opacity-60" />
              </a>
            )}
            <button onClick={() => setOpen(true)} className="btn-red flex items-center gap-2 rounded-lg px-6 py-3 font-semibold" data-testid="write-review-btn">
              <PenLine className="h-4 w-4" /> Write a Review
            </button>
          </div>
        </Reveal>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((r, i) => (
            <Reveal key={r.id || r.name} delay={(i % 3) * 90}>
              <div className="card-hover flex h-full flex-col rounded-2xl border border-white/10 bg-[#111217] p-6" data-testid={`review-card-${i}`}>
                <div className="flex items-start justify-between">
                  <Quote className="h-8 w-8 rotate-180 text-brand/40" />
                  <div className="flex items-center gap-2">
                    {r.isNew && <span className="rounded-md bg-brand/15 px-2 py-0.5 text-[11px] font-bold uppercase text-brand">New</span>}
                    {r.photos.length > 0 && <span className="flex items-center gap-1 rounded-md bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-white/60"><Camera className="h-3 w-3" />{r.photos.length}</span>}
                  </div>
                </div>
                <div className="mt-4"><Stars value={r.rating} /></div>
                <p className="mt-4 flex-1 break-words text-[15px] leading-relaxed text-white/80">"{r.text}"</p>
                {r.photos.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-2" data-testid={`review-photos-${i}`}>
                    {r.photos.map((src, k) => (
                      <button key={src} onClick={() => setLightbox({ photos: r.photos, index: k, review: r })} className="group relative aspect-square overflow-hidden rounded-lg border border-white/10" data-testid={`review-photo-${i}-${k}`}>
                        <img src={src} alt={`${r.car} photo ${k + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      </button>
                    ))}
                  </div>
                )}
                {r.reply?.text && (
                  <div className="mt-4 rounded-lg border-l-2 border-brand bg-brand/[0.06] p-3" data-testid={`review-reply-${i}`}>
                    <div className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.06em] text-brand"><CornerDownRight className="h-3.5 w-3.5" />Reply from EVTTuning UK</div>
                    <p className="mt-1.5 whitespace-pre-line break-words text-[14px] leading-relaxed text-white/75">{r.reply.text}</p>
                  </div>
                )}
                <div className="mt-6 flex items-center gap-3 border-t border-white/10 pt-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand/15 font-display text-lg font-bold uppercase text-brand">{r.name[0]}</div>
                  <div className="min-w-0">
                    <div className="truncate text-[15px] font-semibold text-white" data-testid={`review-name-${i}`}>{r.name}</div>
                    <div className="mt-1 inline-flex max-w-full items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[12.5px] text-white/70" data-testid={`review-car-${i}`}>
                      <Car className="h-3.5 w-3.5 shrink-0 text-brand" /><span className="truncate">{r.car}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {all.length > INITIAL && (
          <div className="mt-10 flex justify-center">
            <button onClick={toggleAll} className="group flex items-center gap-2 rounded-lg border border-white/20 bg-white/[0.03] px-7 py-3.5 font-semibold text-white transition-colors hover:border-brand hover:bg-brand/10" data-testid="toggle-reviews-btn">
              {showAll ? <>Show fewer reviews <ChevronUp className="h-4 w-4" /></> : <>Show all {all.length} reviews <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" /></>}
            </button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto border-white/10 bg-[#111217] text-white" data-testid="review-dialog">
          <DialogTitle className="font-display text-2xl font-bold">Write a Review</DialogTitle>
          <DialogDescription className="text-white/55">Tell other drivers about your EVTTuning UK experience.</DialogDescription>
          <form onSubmit={submit} className="mt-2 space-y-4">
            <div>
              <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">Your Rating</label>
              <Stars value={f.rating} size="h-7 w-7" onChange={(v) => setF({ ...f, rating: v })} hover={hover} setHover={setHover} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">Your Name *</label>
                <Input className={fieldCls} placeholder="John Smith" maxLength={40} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="review-input-name" />
              </div>
              <div>
                <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">Your Car *</label>
                <Input className={fieldCls} placeholder="e.g. BMW 335i" maxLength={40} value={f.car} onChange={(e) => setF({ ...f, car: e.target.value })} data-testid="review-input-car" />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">Your Review *</label>
              <Textarea rows={4} maxLength={400} className={`${fieldCls} h-auto`} placeholder="How did your car feel after the tune?" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="review-input-text" />
              <div className="mt-1 text-right text-xs text-white/35">{f.text.length}/400</div>
            </div>
            <div>
              <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">Photos of your car <span className="normal-case tracking-normal text-white/35">(optional, up to {MAX_PHOTOS})</span></label>
              <div className="grid grid-cols-3 gap-2">
                {photos.map((src, k) => (
                  <div key={k} className="relative aspect-square overflow-hidden rounded-lg border border-white/10" data-testid={`review-upload-preview-${k}`}>
                    <img src={src} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setPhotos((p) => p.filter((_, j) => j !== k))} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white hover:bg-brand" aria-label="Remove photo"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <button type="button" onClick={() => fileRef.current?.click()} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/20 text-white/50 transition-colors hover:border-brand hover:text-white" data-testid="review-add-photo">
                    <ImagePlus className="h-5 w-5" /><span className="text-[11px] font-semibold">Add photo</span>
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} data-testid="review-photo-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-red flex w-full items-center justify-center gap-2 rounded-lg py-3.5 font-semibold disabled:opacity-70" data-testid="review-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} {loading ? "Posting..." : "Post Review"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl border-white/10 bg-[#0b0c0f] p-3 text-white" data-testid="review-lightbox">
          <DialogTitle className="sr-only">Review photo</DialogTitle>
          <DialogDescription className="sr-only">Customer photo</DialogDescription>
          {lightbox && (
            <>
              <img src={lightbox.photos[lightbox.index]} alt={lightbox.review.car} className="max-h-[75vh] w-full rounded-lg object-contain" />
              <div className="flex items-center justify-between px-2 pb-1 pt-3">
                <div className="text-sm text-white/70"><span className="font-semibold text-white">{lightbox.review.name}</span> · {lightbox.review.car}</div>
                {lightbox.photos.length > 1 && (
                  <div className="flex gap-2">
                    {lightbox.photos.map((p, k) => (
                      <button key={p} onClick={() => setLightbox({ ...lightbox, index: k })} className={`h-12 w-12 overflow-hidden rounded-md border ${k === lightbox.index ? "border-brand" : "border-white/15 opacity-60 hover:opacity-100"}`}>
                        <img src={p} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Reviews;
